import { Card, CardContent } from "../../primitives/card";
import { ExternalLink } from "lucide-react";
import { Link, useNavigate } from "react-router";
import type * as API from "@shared-core/data/api-types";
import { isRouteEnabled, paths } from "@shared-core/routing/routes";
import { formatCurrencyGbp, formatCount } from "@shared-core/domain/formatters";
import { CustomerCardHeader } from "./CustomerCardHeader";
import { currentTime } from "@shared-core/domain/current-time";

interface CustomerCardProps {
  customer: API.CustomerIntelligenceListItem;
  onClick?: () => void;
  href?: string;
  onAddTicket?: (customerId: string) => void;
}

export function CustomerCard({
  customer,
  onClick,
  href,
  onAddTicket,
}: CustomerCardProps) {
  const navigate = useNavigate();

  const liveEstimatesPreview = customer.live_estimates_preview || {
    total_amount: 0,
    count: 0,
    most_recent_date: null,
    top: [],
  };
  const liveJobsPreview = customer.live_jobs_preview || {
    total_amount: 0,
    count: 0,
    most_recent_date: null,
    top: [],
  };

  const estimatesTotal = liveEstimatesPreview.total_amount;
  const jobsTotal = liveJobsPreview.total_amount;

  const revenueLast90Days = customer.metrics?.revenue_last_90_days ?? 0;
  const revenuePrevious90Days = customer.metrics?.revenue_previous_90_days ?? 0;
  const revenueChangePct =
    revenuePrevious90Days > 0
      ? ((revenueLast90Days - revenuePrevious90Days) / revenuePrevious90Days) *
        100
      : null;

  // Calculate days since last estimate
  const daysSinceLastEstimate = liveEstimatesPreview.most_recent_date
    ? Math.floor(
        (currentTime().getTime() -
          new Date(liveEstimatesPreview.most_recent_date).getTime()) /
          (1000 * 60 * 60 * 24),
      )
    : null;

  // Calculate days since last job
  const daysSinceLastJob = liveJobsPreview.most_recent_date
    ? Math.floor(
        (currentTime().getTime() -
          new Date(liveJobsPreview.most_recent_date).getTime()) /
          (1000 * 60 * 60 * 24),
      )
    : null;

  const handleCardClick = () => {
    if (href) {
      navigate(href);
    } else if (onClick) {
      onClick();
    }
  };

  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleCardClick();
    }
  };

  const cardContent = (
    <Card
      className={cn(
        "transition-colors hover:bg-muted/50",
        (onClick || href) && "cursor-pointer",
      )}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
      role={onClick || href ? "link" : undefined}
      tabIndex={onClick || href ? 0 : undefined}
    >
      <CardContent className="p-5 space-y-4">
        <CustomerCardHeader
          customer={customer}
          href={href}
          onAddTicket={onAddTicket}
        />

        {/* Single Row with Revenue, Estimates, and Jobs */}
        <div className="grid grid-cols-1 md:grid-cols-[150px_1fr_1fr] gap-3">
          {/* Revenue Column */}
          <div className="border border-border rounded-lg p-2 bg-muted/30">
            <div className="text-xs text-muted-foreground mb-2">Revenue</div>
            <div className="space-y-2">
              <div>
                <div className="text-sm font-bold leading-none">
                  {formatCurrencyGbp(
                    customer.metrics?.revenue_last_12_months ?? 0,
                  )}
                </div>
                <div className="text-[10px] text-muted-foreground/70 mt-0.5">
                  Last 12 months
                </div>
              </div>
              <div>
                <div className="flex items-baseline gap-1">
                  <div className="text-sm font-bold leading-none">
                    {formatCurrencyGbp(revenueLast90Days)}
                  </div>
                  {revenueChangePct !== null && (
                    <span
                      className={
                        revenueChangePct >= 0
                          ? "text-[10px] font-medium text-green-700"
                          : "text-[10px] font-medium text-red-700"
                      }
                      title="Revenue last 90 days vs. the prior 90 days"
                    >
                      {revenueChangePct >= 0 ? "▲" : "▼"}
                      {revenueChangePct > 0 ? "+" : ""}
                      {revenueChangePct.toFixed(0)}%
                    </span>
                  )}
                </div>
                <div className="text-[10px] text-muted-foreground/70 mt-0.5">
                  Last 90 days
                </div>
              </div>
            </div>
          </div>

          {/* Live Estimates Column */}
          <div className="border border-border rounded-lg p-2 bg-muted/30 min-w-0">
            <div className="flex items-center justify-between mb-2 gap-2">
              <div className="text-xs font-medium text-muted-foreground truncate">
                Live Estimates
                {daysSinceLastEstimate !== null && (
                  <span className="ml-1 text-muted-foreground/60">
                    · {daysSinceLastEstimate}d since last estimate
                  </span>
                )}
              </div>
              {liveEstimatesPreview.count > 0 && (
                <Link
                  to={paths.estimates({
                    customerId: customer.id,
                    status: "open",
                  })}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                  }}
                  className="text-xs text-primary hover:underline inline-flex items-center gap-0.5 shrink-0 px-2 py-1 -mx-2 -my-1 rounded"
                >
                  All
                  <ExternalLink className="size-3" />
                </Link>
              )}
            </div>
            {liveEstimatesPreview.count === 0 ? (
              <div className="text-xs text-muted-foreground">None</div>
            ) : (
              <>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-xl font-bold leading-none">
                    {formatCurrencyGbp(estimatesTotal)}
                  </span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    ({formatCount(liveEstimatesPreview.count)})
                  </span>
                </div>
                <div className="border-t pt-1.5">
                  <div className="space-y-1 text-xs">
                    {(liveEstimatesPreview.top ?? []).map((estimate) => (
                      <div
                        key={estimate.id}
                        className="flex items-start gap-1 min-w-0"
                      >
                        <span className="font-medium shrink-0">
                          {formatCurrencyGbp(estimate.amount)}
                        </span>
                        <span className="text-muted-foreground/60">•</span>
                        <span className="text-muted-foreground/60 shrink-0">
                          {estimate.source_system_id}
                        </span>
                        <span className="text-muted-foreground/60">•</span>
                        <span className="text-muted-foreground truncate">
                          {estimate.description}
                        </span>
                      </div>
                    ))}
                    {liveEstimatesPreview.count > 3 && (
                      <div className="text-muted-foreground/60">
                        ... +{formatCount(liveEstimatesPreview.count - 3)} more
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Live Jobs Column */}
          <div className="border border-border rounded-lg p-2 bg-muted/30 min-w-0">
            <div className="flex items-center justify-between mb-2 gap-2">
              <div className="text-xs font-medium text-muted-foreground truncate">
                Live jobs
                {daysSinceLastJob !== null && (
                  <span className="ml-1 text-muted-foreground/60">
                    · {daysSinceLastJob}d since last job
                  </span>
                )}
              </div>
              {liveJobsPreview.count > 0 && isRouteEnabled("jobs") && (
                <Link
                  to={paths.jobs({ customerId: customer.id, bucket: "wip" })}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                  }}
                  className="text-xs text-primary hover:underline inline-flex items-center gap-0.5 shrink-0 px-2 py-1 -mx-2 -my-1 rounded"
                >
                  All
                  <ExternalLink className="size-3" />
                </Link>
              )}
            </div>
            {liveJobsPreview.count === 0 ? (
              <div className="text-xs text-muted-foreground">None</div>
            ) : (
              <>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="text-xl font-bold leading-none">
                    {formatCurrencyGbp(jobsTotal)}
                  </span>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    ({formatCount(liveJobsPreview.count)})
                  </span>
                </div>
                <div className="border-t pt-1.5">
                  <div className="space-y-1 text-xs">
                    {(liveJobsPreview.top ?? []).map((job) => (
                      <div
                        key={job.id}
                        className="flex items-start gap-1 min-w-0"
                      >
                        <span className="font-medium shrink-0">
                          {formatCurrencyGbp(job.amount)}
                        </span>
                        <span className="text-muted-foreground/60">•</span>
                        <span className="text-muted-foreground/60 shrink-0">
                          {job.source_system_id}
                        </span>
                        <span className="text-muted-foreground/60">•</span>
                        <span className="text-muted-foreground truncate">
                          {job.description}
                        </span>
                      </div>
                    ))}
                    {liveJobsPreview.count > 3 && (
                      <div className="text-muted-foreground/60">
                        ... +{formatCount(liveJobsPreview.count - 3)} more
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return cardContent;
}

function cn(...classes: (string | undefined | false)[]) {
  return classes.filter(Boolean).join(" ");
}
